import { defaultSiteLanguage, getCleanID, getQueryStringParameter } from 'lib/utils';
import { ParsedUrlQuery } from 'querystring';
import { County } from 'models/plans-search-types';
import { getUTCdate } from 'lib/utils';
import { PlanYear } from './Types/PlanYear.Type';
import { DrugCoverageTableGroup, UsedOption } from './Types/DrugCoverageTables.Type';

/**
 *Builds out the URL with all query strings attached
 *
 * @param {string} url The base url the start with
 * @param {ParsedUrlQuery} query An existing query string from router.query
 * @param {[string,string][]} newQueries An array of sets of strings used to create new querystrings [name, value]
 * @returns {string} new url
 *
 */
export function buildURL(
  url: string,
  query: ParsedUrlQuery | null,
  newQueries: [string, string][] = [],
  currentLanguage = ''
): string {
  url = url + '?';

  newQueries.forEach((queryItem) => {
    url = url + (url.endsWith('?') ? '' : '&') + queryItem[0] + '=' + queryItem[1];
  });

  if (currentLanguage && currentLanguage !== defaultSiteLanguage && !url.startsWith(`/${currentLanguage}/`)) {
    url = `/${currentLanguage}${url}`;
  }

  if (query === null) {
    return url;
  }

  const state = getQueryStringParameter('state', query);
  const countyName = getQueryStringParameter('county', query);
  const year = getQueryStringParameter('year', query);
  const zipcode = getQueryStringParameter('zipcode', query);
  const member = getQueryStringParameter('ExistingMember', query);
  if (state !== '') {
    url = url + '&state=' + state;
  }
  if (countyName !== '') {
    url = url + '&county=' + countyName;
  }
  if (year !== '') {
    url = url + '&year=' + year;
  }
  if (zipcode !== '') {
    url = url + '&zipcode=' + zipcode;
  }
  if (member !== '') {
    url = url + '&ExistingMember=' + member;
  }

  return url;
}

/**
 *Builds URL to apply for given plan
 *
 * @param {string} type name of plantype for given plan
 * @param {string} zipcode zipcode of given plan
 * @param {string} county county of given plan
 * @param {string} year year of given plan
 * @param {string} planName name of given plan
 * @param {string | number | undefined} contract contract of given plan
 * @param {string | number | undefined} pbp pbp of given plan
 * @param {string | number | undefined} groupId groupId of given plan
 * @param {string | number | undefined} cavulusPlanName Cavulus Plan Name of given plan
 * @returns url
 */
export function buildApplyToEnrollURL(
  type: string,
  zipcode: string,
  county: string,
  year: string,
  planName: string | number | undefined,
  contract: string | number | undefined,
  pbp: string | number | undefined,
  groupId: string | number | undefined,
  cavulusPlanName: string | number | undefined
) {
  const planApplyNowUrl = process.env['NEXT_PUBLIC_PLAN_APPLY_NOW_URL'] || '';
  const queries: [string, string][] = Array(6);
  queries.push(['form_data[GoogleCampaignID]', type + '-Shop']);
  queries.push(['form_data[ApplicantZip]', zipcode]);
  queries.push(['[ApplicantCounty]', county]);
  queries.push(['[PlanYear]', year]);
  if (planName !== undefined) {
    queries.push(['[Description]', planName.toString()]);
  }
  queries.push(['[ProductID]', year + '-' + contract + '-' + pbp + '-' + groupId + '||' + cavulusPlanName]);
  return buildURL(planApplyNowUrl, null, queries);
}

export const checkPlans = (county: County, year: string) => {
  let isInError = false;
  const keys = Object.keys(county.PlansByYear);

  if (keys.length == 0 || keys.indexOf(year) == -1) {
    isInError = true;
  }
  return isInError;
};

/**
 *
 * @param {YearItem[]} planYears PlanYears data puled from Global Data in Sitecore
 * @param {string} selectedYear The plan year selected by the user
 * @returns {boolean} Should enroll button be enabled?
 */
export function checkEnrollEnable(planYears: PlanYear[], selectedYear: number) {
  const matchingYear = planYears?.find((x) => x.year == selectedYear);
  const now = getUTCdate(new Date());
  const enrollButtonStartDate = getUTCdate(matchingYear?.enrollButtonStart ?? new Date(3000, 12, 31));
  return enrollButtonStartDate && enrollButtonStartDate <= now;
}

export function getMatchingDrugCoverageGroupOption(groups: DrugCoverageTableGroup[], matchID: string) {
  let newGroup: UsedOption = {
    drugCoverageTables: [],
    groupCaption: '',
    planComparisonTableHeader: '',
  };
  for (let x = 0; x < groups.length; x++) {
    const matchedOption = groups[x].options.find((opt) => getCleanID(opt.id) == matchID);
    if (matchedOption == undefined) {
      continue;
    } else {
      newGroup = {
        drugCoverageTables: matchedOption.drugCoverageTables,
        groupCaption: groups[x].groupCaption,
        planComparisonTableHeader: groups[x].planComparisonTableHeader,
      };
      break;
    }
  }
  return newGroup;
}
